/**
 * ChessDiagram — read-only chess board renderer.
 *
 * Props:
 *   fen   {string}  FEN string to display
 *   prefs {object}  Diagram preferences (from AuthContext):
 *                   { diagramPieceSet, diagramLightSquare, diagramDarkSquare,
 *                     diagramBorderColor, diagramCoordPosition, diagramFlipped }
 *   size  {number}  Board pixel size (default 360)
 *
 * The piece cache is module-level so all ChessDiagram instances on a page
 * (e.g. the 20-card Dashboard) share a single fetch per piece set.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  parseFen,
  buildBoardSvg,
  fetchPieceSet,
  PIECE_SETS_BY_KEY,
  DEFAULT_LIGHT_SQUARE,
  DEFAULT_DARK_SQUARE,
  DEFAULT_BORDER_COLOR,
  DEFAULT_SHOW_SIDE_TO_MOVE_DISC,
} from "../pages/ChessDiagramGenerator";

// ── Module-level shared cache ─────────────────────────────────────────────
// Key: pieceSetKey  →  Value: { data, error, promise }
// This ensures that even if 20 ChessDiagram instances mount simultaneously,
// only one fetch is made per piece set.
const pieceCache = {};

/**
 * Returns cached piece data for a set key, or null if not yet loaded.
 * PDF generation can use this to re-use already-fetched pieces.
 */
export function getPieceData(setKey) {
  return pieceCache[setKey]?.data ?? null;
}

function usePieceSet(pieceSet, setMeta) {
  // eslint-disable-next-line
  const [tick, setTick] = useState(0); // used only to trigger re-render after load

  useEffect(() => {
    if (setMeta.type !== "lichess") return;

    const entry = pieceCache[pieceSet];
    if (entry && (entry.data || entry.error)) return; // already resolved

    if (!entry) {
      // First request for this set — start the fetch and store the promise
      const controller = new AbortController();
      const promise = fetchPieceSet(pieceSet, controller.signal)
        .then(data => { pieceCache[pieceSet] = { data, error: null }; })
        .catch(e  => {
          if (e.name !== "AbortError")
            pieceCache[pieceSet] = { data: null, error: e.message };
        })
        .finally(() => setTick(t => t + 1)); // notify all subscribers

      pieceCache[pieceSet] = { data: null, error: null, promise, controller };
    }
    // Subsequent instances just wait for the shared promise to resolve
    const p = pieceCache[pieceSet].promise;
    if (p) p.then(() => setTick(t => t + 1));

    return () => { /* intentionally no abort — other instances may still need it */ };
  }, [pieceSet, setMeta.type]); // eslint-disable-line react-hooks/exhaustive-deps

  const entry = pieceCache[pieceSet];
  return {
    pieceData: entry?.data ?? null,
    loadError: entry?.error ?? null,
    loading:   setMeta.type === "lichess" && (!entry || (!entry.data && !entry.error)),
  };
}

// ─────────────────────────────────────────────────────────────────────────────

export default function ChessDiagram({ fen, prefs = {}, size = 360 }) {
  const pieceSet            = prefs.diagramPieceSet            ?? "cburnett";
  const lightSquare         = prefs.diagramLightSquare         ?? DEFAULT_LIGHT_SQUARE;
  const darkSquare          = prefs.diagramDarkSquare          ?? DEFAULT_DARK_SQUARE;
  const borderColor         = prefs.diagramBorderColor         ?? DEFAULT_BORDER_COLOR;
  const coordPosition       = prefs.diagramCoordPosition       ?? "border";
  const flipped             = prefs.diagramFlipped             ?? false;
  const showSideToMoveDisc  = prefs.diagramShowSideToMoveDisc  ?? DEFAULT_SHOW_SIDE_TO_MOVE_DISC;

  const setMeta = PIECE_SETS_BY_KEY[pieceSet] ?? PIECE_SETS_BY_KEY.unicode;
  const { pieceData, loadError, loading } = usePieceSet(pieceSet, setMeta);

  const svg = useMemo(() => {
    if (!fen) return null;
    if (setMeta.type === "lichess" && !pieceData) return null;
    try {
      const { board, activeColor } = parseFen(fen);
      return buildBoardSvg({
        board, activeColor, flipped,
        setKey: pieceSet,
        pieceData: setMeta.type === "lichess" ? pieceData : null,
        boardSize: size,
        lightSquare, darkSquare, borderColor, coordPosition, showSideToMoveDisc,
      });
    } catch {
      return null;
    }
  }, [fen, pieceSet, pieceData, flipped, size,
      lightSquare, darkSquare, borderColor, coordPosition, showSideToMoveDisc, setMeta.type]);

  if (!fen) return null;

  if (loading) return (
    <div className="text-center text-muted small py-2">
      <div className="spinner-border spinner-border-sm me-1" />
      Loading pieces…
    </div>
  );

  if (loadError) return (
    <div className="text-muted small fst-italic">
      Could not load piece set — check your connection.
    </div>
  );

  if (!svg) return (
    <div className="text-muted small fst-italic">Invalid FEN.</div>
  );

  return (
    <div style={{ lineHeight: 0, display: "inline-block" }}
         dangerouslySetInnerHTML={{ __html: svg }} />
  );
}
